import { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { X, Shield, Building2, FileText, User, UserX, ChevronDown, Pencil, Copy, ExternalLink, Check, Clock, RotateCcw, UserCheck, Plus, Lock, Search, Loader2, ArrowRight } from "lucide-react";
import {
  Button,
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
  Skeleton,
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent,
  DropdownMenuItem, DropdownMenuSeparator,
  Tooltip, TooltipTrigger, TooltipContent, TooltipProvider,
  SearchableCombobox,
} from "@nexus/ui";
import { cn } from "@nexus/ui";
import type { UserItem, UserAccessEntry } from "@/features/identity-hub/api/identity-hub.api";
import { orgColor, isSuperTenantMember, tenantAccentColor } from "@/features/identity-hub/utils/iam.utils";
import { relativeTimeI18n } from "@/features/identity-hub/utils/time";
import {
  ORG_ACCENT_DRAWER_TOP_CLASS,
  ORG_ACCENT_HEADER_WASH_CLASS,
  orgAccentRowSurfaceStyle,
  orgAccentRowWashStyle,
  orgAccentRowWashStyleDark,
  orgAccentRowWashStyleDarkHover,
  orgAccentVar,
  SUPER_TENANT_ACCENT,
} from "@/features/identity-hub/utils/org-accent-surface";
import { OrgLogoMark } from "./OrgLogoMark";
import { useOrgToggleHandler, useBulkGrantAccess, useGrantAccess, useGrantAccessTemplate, useRevokeAccess, useReplaceAccess, useRevokeAccessTemplate, useRegenerateInvite, useInviteLink, syncSidebarIfActiveUser, type DerivedTenant } from "@/features/identity-hub/hooks/useIdentityHub";
import { useRoleSwitchInFlight } from "@/features/identity-hub/hooks/useRoleSwitchInFlight";
import { useCopyToClipboard } from "@/hooks/useCopyToClipboard";
import { usePermissions, useIsSuperTenant } from "@/features/auth/hooks/usePermissions";
import { ImagePreviewTrigger } from "./ImagePreviewTrigger";
import { UserPhotoMark } from "./UserPhotoMark";
import { AuditTimeline, useAuditLogBadgeCount } from "./AuditTimeline";
import { SuperTenantBadge } from "./SuperTenantBadge";
import { AuthProviderBadge } from "./shared/AuthProviderBadge";
import { UserStatusBadge } from "./shared/UserStatusBadge";
import { RowActionsTrigger } from "./shared/RowActionsTrigger";
import { DrawerTabStrip, type DrawerTabDef } from "./shared/drawer/DrawerTabStrip";
import { IamOverlayDrawer } from "./shared/drawer/IamOverlayDrawer";
import { DrawerStat } from "./shared/drawer/DrawerStat";
import { PersonProfileForm } from "./PersonProfileForm";
import { PREVENT_DISMISS_PROPS } from "./shared/dialog/prevent-dismiss-on-outside";
import type { AccessGroupItem } from "@/features/identity-hub/api/identity-hub.api";
import { buildInviteUrl } from "@/features/auth/lib/invite";
import {
  catalogTemplateId,
  grantableRolesOnOrg,
  isFixedCatalogAccess,
  isPerTenantAssignable,
  perTenantAssignableGroups,
  uniqueFixedCatalogRoles,
} from "@/features/identity-hub/lib/access-scope";
import { filterGrantableByPermissions } from "@/features/identity-hub/lib/grantable-role";
import { useGrantablePermissionTupleKeys } from "@/features/identity-hub/hooks/useGrantableCatalogKeys";
import {
  parseTemplateRoleOptionId,
  unprojectedWildcardOptions,
  type InviteWildcardTemplate,
} from "@/features/identity-hub/lib/invite-role-options";

// ─── Status pill ──────────────────────────────────────────────────────────────

// ─── Org chip ─────────────────────────────────────────────────────────────────

function OrgChip({
  name,
  size = 20,
  logoUrl,
  accent,
  isSuperTenant = false,
}: {
  name: string;
  size?: number;
  logoUrl?: string | null;
  accent?: string;
  isSuperTenant?: boolean;
}) {
  return <OrgLogoMark name={name} logoUrl={logoUrl} accent={accent} isSuperTenant={isSuperTenant} size={size} />;
}

// ─── Time helpers ─────────────────────────────────────────────────────────────

type ExpirySeverity = "expired" | "warning" | "ok";

function expiresIn(isoString: string): { tKey: string; count?: number; severity: ExpirySeverity } {
  const diff = new Date(isoString).getTime() - Date.now();
  if (diff <= 0) return { tKey: "iam.personDrawer.invite.expired", severity: "expired" };
  const d = Math.ceil(diff / 86_400_000);
  if (d >= 1) return { tKey: "iam.personDrawer.invite.daysLeft", count: d, severity: d < 3 ? "warning" : "ok" };
  const h = Math.ceil(diff / 3_600_000);
  return { tKey: "iam.personDrawer.invite.hoursLeft", count: h, severity: "warning" };
}

// ─── Copy invite link button ──────────────────────────────────────────────────
//
// Copies the invite's EXISTING link via the read-only GET /invites/:id/link
// endpoint. It must NOT regenerate — that would expire the link already emailed
// to the invitee (AD-1714). The token is never stored after the copy.

interface CopyInviteButtonProps {
  disabled?: boolean;
  onCopy: (
    callbacks: { onSuccess: (data: { token: string }) => void; onError: () => void },
  ) => void;
  isPending: boolean;
}

function CopyInviteButton({ disabled, onCopy, isPending }: CopyInviteButtonProps) {
  const { t } = useTranslation();
  const { copied, copy: copyToClipboard } = useCopyToClipboard();

  const copy = () => {
    onCopy({
      onSuccess: (data) => void copyToClipboard(buildInviteUrl(data.token)),
      onError: () => { /* parent toast handles surfacing */ },
    });
  };

  return (
    <button
      onClick={copy}
      disabled={disabled || isPending}
      className={cn(
        "inline-flex items-center gap-1.5 rounded-md px-2 py-1 text-[11px] font-medium transition-colors",
        copied
          ? "text-success"
          : "text-muted-foreground hover:bg-muted hover:text-foreground",
        "disabled:pointer-events-none disabled:opacity-40",
      )}
    >
      {copied ? <Check className="h-3 w-3 shrink-0" /> : <Copy className="h-3 w-3 shrink-0" />}
      {copied ? t("iam.personDrawer.invite.copied") : t("iam.personDrawer.invite.copyLink")}
    </button>
  );
}

// ─── Types ────────────────────────────────────────────────────────────────────

export type PersonDrawerTab = "profile" | "access" | "audit";

export interface TenantItem {
  id: string;
  name: string;
  type: string;
  logoUrl?: string | null;
  brandColor?: string | null;
}

export interface PersonDrawerProps {
  user: UserItem | null;
  access: UserAccessEntry[];
  allTenants: TenantItem[];
  accessGroupsByTenant: Map<string, AccessGroupItem[]>;
  onClose: () => void;
  open?: boolean;
  initialTab?: PersonDrawerTab;
  activeTenantId: string | null;
  loggedInEmail: string | undefined;
  onSuspend?: () => void;
  onUnsuspend?: () => void;
  onJumpToRole?: (accessId: string) => void;
  onJumpToOrg?: (tenantId: string) => void;
  onCreateRole?: (tenantId: string) => void;
  /** True until this person's access batch has produced a result. Missing access is not zero orgs. */
  isAccessLoading?: boolean;
  /** True when that batch failed and there is no cached org list to show. */
  isAccessError?: boolean;
  onRetryAccess?: () => void;
  onOpenAuditLog?: (options?: { highlightId?: string | null; searchPrefill?: string | null; category?: string | null }) => void;
  /** Active Wildcard catalog roles. Passed only for platform superadmins. */
  wildcardTemplates?: InviteWildcardTemplate[];
  /** True while that catalog is still loading, so an empty Replace list is not final. */
  wildcardTemplatesLoading?: boolean;
}

type ReplaceRoleChoice = {
  id: string;
  name: string;
  tenantId: string;
};

const ACCESS_ORG_VISIBLE_DEFAULT = 8;

// ─── PersonDrawer ─────────────────────────────────────────────────────────────

export function PersonDrawer({ user, access, allTenants, accessGroupsByTenant, onClose, open = true, initialTab = "access", activeTenantId, loggedInEmail, onSuspend, onUnsuspend, onJumpToOrg, onCreateRole, onOpenAuditLog, isAccessLoading = false, isAccessError = false, onRetryAccess, wildcardTemplates = [], wildcardTemplatesLoading = false }: PersonDrawerProps) {
  const { t } = useTranslation();
  const perms = usePermissions();
  const isSuperTenant = useIsSuperTenant();
  const grantableKeys = useGrantablePermissionTupleKeys();
  const canViewAuditLog = isSuperTenant || perms.IDENTITY_HUB_VIEW_USER_AUDIT_LOG.isOn;
  const canReplaceAccess = isSuperTenant || perms.IDENTITY_HUB_REPLACE_ACCESS.isOn;
  const canRevokeTemplate = isSuperTenant || perms.IDENTITY_HUB_REVOKE_TEMPLATE.isOn;
  const canCopyInviteLink   = isSuperTenant || perms.IDENTITY_HUB_GET_INVITE_LINK.isOn;
  const canRegenerateInvite = isSuperTenant || perms.IDENTITY_HUB_REGENERATE_INVITE.isOn;
  const isViewingSelf = !!loggedInEmail && user?.email === loggedInEmail;
  // Only platform admins may edit their own access — block all self-mutations otherwise.
  const selfEditLocked = isViewingSelf && !isSuperTenant;
  const grantableAssignableGroups = (groups: AccessGroupItem[]) =>
    filterGrantableByPermissions(perTenantAssignableGroups(groups), grantableKeys);
  const [tab, setTab] = useState<PersonDrawerTab>(initialTab);
  const [accessExpanded, setAccessExpanded] = useState(false);
  const handleClose = () => onClose();
  const [inviteCooldown, setInviteCooldown] = useState(0);
  const cooldownTimer = useRef<ReturnType<typeof setInterval> | undefined>(undefined);
  const [pendingRolePick, setPendingRolePick] = useState<{
    tenant: DerivedTenant;
    groups: AccessGroupItem[];
  } | null>(null);
  const [pendingAccessRemove, setPendingAccessRemove] = useState<{
    entry: UserAccessEntry;
  } | null>(null);
  const [pendingRoleEdit, setPendingRoleEdit] = useState<UserAccessEntry | null>(null);
  const [selectedRoleForEdit, setSelectedRoleForEdit] = useState<ReplaceRoleChoice | null>(null);
  const replaceChoicesFor = (entry: UserAccessEntry): ReplaceRoleChoice[] => {
    const allOrgRoles = accessGroupsByTenant.get(entry.access.tenantId) ?? [];
    const live = grantableAssignableGroups(
      allOrgRoles.filter((group) => !group.deletedAt && group.id !== entry.accessId),
    ).map((group) => ({ id: group.id, name: group.name, tenantId: group.tenantId }));
    if (!isSuperTenant) return live;
    const catalog = unprojectedWildcardOptions(allOrgRoles, wildcardTemplates).map((option) => ({
      id: option.id,
      name: option.name,
      tenantId: entry.access.tenantId,
    }));
    return [...live, ...catalog];
  };
  const replaceChoices = pendingRoleEdit ? replaceChoicesFor(pendingRoleEdit) : [];
  const replaceChoiceKey = replaceChoices.map((choice) => choice.id).join("\0");
  const replaceChoicesRef = useRef(replaceChoices);
  replaceChoicesRef.current = replaceChoices;
  const [pendingOrgRemove, setPendingOrgRemove] = useState<{
    tenantId: string;
    tenantName: string;
    roleCount: number;
  } | null>(null);
  const [removingOrgId, setRemovingOrgId] = useState<string | null>(null);
  const [orgComboboxOpen, setOrgComboboxOpen] = useState(false);
  const [confirmAddAllOpen, setConfirmAddAllOpen] = useState(false);
  const [pendingFixedRevoke, setPendingFixedRevoke] = useState<{
    templateId: string;
    name: string;
    orgIds: string[];
  } | null>(null);

  const regenerateInviteMutation = useRegenerateInvite();
  const inviteLinkMutation = useInviteLink();

  const startInviteCooldown = () => {
    clearInterval(cooldownTimer.current);
    setInviteCooldown(30);
    cooldownTimer.current = setInterval(() => {
      setInviteCooldown((s) => {
        if (s <= 1) { clearInterval(cooldownTimer.current); return 0; }
        return s - 1;
      });
    }, 1000);
  };

  // Reset to open state whenever a new user is shown
  useEffect(() => {
    clearInterval(cooldownTimer.current);
    setTab(initialTab);
    setAccessExpanded(false);
    setInviteCooldown(0);
    setPendingRolePick(null);
    setPendingAccessRemove(null);
    setPendingRoleEdit(null);
    setSelectedRoleForEdit(null);
    setPendingOrgRemove(null);
    setRemovingOrgId(null);
    setOrgComboboxOpen(false);
    setPendingFixedRevoke(null);
    regenerateInviteMutation.reset();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, initialTab]);

  useEffect(() => () => clearInterval(cooldownTimer.current), []);

  // A single alternative role is a binary choice, not a picker — preselect it so
  // the confirm action is reachable in one click instead of "pick, then confirm".
  // Keep a selection that is still in the list when the catalog finishes loading.
  useEffect(() => {
    if (!pendingRoleEdit) {
      setSelectedRoleForEdit(null);
      return;
    }
    const choices = replaceChoicesRef.current;
    setSelectedRoleForEdit((current) => {
      if (current && choices.some((choice) => choice.id === current.id)) return current;
      return choices.length === 1 ? (choices[0] ?? null) : null;
    });
  }, [pendingRoleEdit, replaceChoiceKey]);



  // Unique orgs this user belongs to
  const orgMap = new Map<string, { id: string; name: string; type: string; roles: UserAccessEntry[] }>();
  for (const entry of access) {
    const { tenantId, tenant } = entry.access;
    if (!orgMap.has(tenantId)) {
      orgMap.set(tenantId, { id: tenantId, name: tenant.name, type: tenant.type, roles: [] });
    }
    orgMap.get(tenantId)!.roles.push(entry);
  }
  const memberOrgs = Array.from(orgMap.values());

  const { handleOrgToggle, isPending: orgTogglePending, lockedTenantId } = useOrgToggleHandler(
    user?.id ?? "",
    user?.email,
    access,
    memberOrgs.length,
    loggedInEmail,
    activeTenantId,
    { onRoleRequired: (tenant, groups) => setPendingRolePick({ tenant, groups }) },
  );

  const bulkGrant = useBulkGrantAccess();

  // Access tab — entry-level revoke/grant/replace (with sidebar sync for self-edits)
  const entryRevoke   = useRevokeAccess  ({ beforeToast: () => syncSidebarIfActiveUser(user?.email) });
  const entryGrant    = useGrantAccess   ({ beforeToast: () => syncSidebarIfActiveUser(user?.email) });
  const entryReplace  = useReplaceAccess ({ beforeToast: () => syncSidebarIfActiveUser(user?.email) });
  const grantCatalogRole = useGrantAccessTemplate();
  const revokeTemplate = useRevokeAccessTemplate({ beforeToast: () => syncSidebarIfActiveUser(user?.email) });
  const roleSwitchInFlight = useRoleSwitchInFlight();
  const entryPending  = entryRevoke.isPending || entryGrant.isPending || entryReplace.isPending || revokeTemplate.isPending || grantCatalogRole.isPending;
  const addingAccessId = entryGrant.isPending ? entryGrant.variables?.accessId ?? null : null;
  const addingFixedTemplateId = grantCatalogRole.isPending
    ? grantCatalogRole.variables?.templateId ?? null
    : null;

  const grantOrgRole = (group: AccessGroupItem, onSettled?: () => void) => {
    if (!user) return;
    if (isFixedCatalogAccess(group)) {
      const templateId = catalogTemplateId(group);
      if (!templateId) return;
      grantCatalogRole.mutate(
        { templateId, tenantId: activeTenantId ?? group.tenantId, userIds: [user.id] },
        { onSettled },
      );
      return;
    }
    entryGrant.mutate(
      { userId: user.id, accessId: group.id, tenantId: group.tenantId },
      { onSettled },
    );
  };

  const isAddingGroup = (group: AccessGroupItem) => {
    if (addingAccessId === group.id) return true;
    const templateId = catalogTemplateId(group);
    return !!templateId && addingFixedTemplateId === templateId;
  };
  const fixedRoles = uniqueFixedCatalogRoles(access);

  const handleAccessRemoveClick = (entry: UserAccessEntry) => {
    if (!user) return;
    if (isFixedCatalogAccess(entry.access)) return;
    const tenantId = entry.access.tenantId;
    const otherOrgEntries = access.filter((e) => e.access.tenantId === tenantId && e.id !== entry.id);

    if (otherOrgEntries.length > 0) {
      // Case C: user keeps other roles in this org — silent revoke
      entryRevoke.mutate({ userId: user.id, accessId: entry.accessId, tenantId });
      return;
    }

    // Last role in org. Before showing the destructive confirm, check
    // whether the org has any other access group the user could be moved to.
    // The destructive "remove from org" path should only fire when there's
    // genuinely nowhere else for the user to land — otherwise the X-button
    // doubles as an inline role switcher (much closer to what users mean
    // when they nudge the role chip).
    const alternativeRoles = replaceChoicesFor(entry);
    const catalogStillLoading = isSuperTenant && wildcardTemplatesLoading && alternativeRoles.length === 0;

    if (alternativeRoles.length > 0 || catalogStillLoading) {
      setPendingRoleEdit(entry);
      return;
    }

    // Truly the last possible role in this org — show removal confirm.
    setPendingAccessRemove({ entry });
  };

  const handleAccessRemoveConfirm = () => {
    if (!pendingAccessRemove || !user) return;
    if (isFixedCatalogAccess(pendingAccessRemove.entry.access)) return;
    entryRevoke.mutate({
      userId: user.id,
      accessId: pendingAccessRemove.entry.accessId,
      tenantId: pendingAccessRemove.entry.access.tenantId,
    });
    setPendingAccessRemove(null);
  };

  const handleAccessEditClick = (entry: UserAccessEntry) => {
    if (!user) return;
    if (isFixedCatalogAccess(entry.access)) return;
    setPendingRoleEdit(entry);
  };

  const handleAccessRoleSwitch = (choice: ReplaceRoleChoice) => {
    if (!pendingRoleEdit || !user) return;
    if (entryPending || !roleSwitchInFlight.tryBegin()) return;
    const templateId = parseTemplateRoleOptionId(choice.id);
    const onSettled = () => {
      roleSwitchInFlight.end();
      setPendingRoleEdit(null);
    };
    if (templateId) {
      grantCatalogRole.mutate(
        {
          templateId,
          tenantId: choice.tenantId,
          userIds: [user.id],
          tenantIds: [choice.tenantId],
        },
        { onSettled },
      );
      return;
    }
    entryReplace.mutate(
      {
        userId: user.id,
        oldAccessId: pendingRoleEdit.accessId,
        newAccessId: choice.id,
        tenantId: choice.tenantId,
      },
      { onSettled },
    );
  };
  const bulkPending = bulkGrant.isPending;

  const canEditOrgMembership = !selfEditLocked && !isAccessLoading && !isAccessError;

  const handleAddToAll = () => {
    if (!user || !canEditOrgMembership) return;
    const grants = allTenants
      .filter((t) => !orgMap.has(t.id))
      .flatMap((t) => {
        const groups = grantableAssignableGroups(
          (accessGroupsByTenant.get(t.id) ?? []).filter((g) => !g.deletedAt),
        );
        return groups.length > 0 ? [{ accessId: groups[0].id, tenantId: t.id }] : [];
      });
    if (grants.length === 0) return;
    bulkGrant.mutate(
      { userId: user.id, grants, tenantId: activeTenantId ?? grants[0].tenantId },
      { onSuccess: () => setConfirmAddAllOpen(false) },
    );
  };

  // Orgs tab — non-member tenants that have at least one active (non-archived) assignable role
  const joinableTenants = allTenants
    .filter((t) => !orgMap.has(t.id))
    .map((t) => ({
      ...t,
      roles: grantableRolesOnOrg(
        accessGroupsByTenant.get(t.id) ?? [],
        grantableKeys,
        { includeFixedCatalog: isSuperTenant },
      ),
    }))
    .filter((t) => t.roles.length > 0);

  // Add an org via combobox: single role auto-grants; multiple roles surface the inline picker.
  const handleOrgAdd = (tenant: { id: string; name: string; roles: AccessGroupItem[] }) => {
    if (!user) return;
    if (tenant.roles.length === 1) {
      grantOrgRole(tenant.roles[0], () => setOrgComboboxOpen(false));
      return;
    }
    setOrgComboboxOpen(false);
    setPendingRolePick({
      tenant: { id: tenant.id, name: tenant.name, isActive: false, accessEntries: [] },
      groups: tenant.roles,
    });
  };

  // Remove an org membership: confirm first, then revoke all entries via the org-toggle handler
  const handleOrgRemoveConfirm = async () => {
    if (!pendingOrgRemove) return;
    const org = orgMap.get(pendingOrgRemove.tenantId);
    const tenantId = pendingOrgRemove.tenantId;
    setPendingOrgRemove(null);
    if (!org) return;
    setRemovingOrgId(tenantId);
    try {
      await handleOrgToggle(
        { id: org.id, name: org.name, isActive: true, accessEntries: org.roles },
        false,
      );
    } finally {
      setRemovingOrgId(null);
    }
  };

  const handleFixedRevokeConfirm = () => {
    if (!pendingFixedRevoke || !user || !activeTenantId) return;
    const remaining = access.filter(
      (entry) => catalogTemplateId(entry.access) !== pendingFixedRevoke.templateId,
    );
    if (remaining.length === 0) return;
    revokeTemplate.mutate(
      { templateId: pendingFixedRevoke.templateId, tenantId: activeTenantId, userIds: [user.id] },
      { onSettled: () => setPendingFixedRevoke(null) },
    );
  };

  // Audit count for the tab badge — reflects the first-page count only: a
  // stable, cheap "is there activity?" signal that doesn't grow as the user
  // scrolls deeper into history.
  const auditCount = useAuditLogBadgeCount("user", user?.id, { enabled: canViewAuditLog });

  if (!user) return null;

  const drawerAccentColor = orgColor(user.displayName);

  const tabs: Array<DrawerTabDef<PersonDrawerTab>> = [
    { id: "profile", label: t("iam.personDrawer.tabs.profile"), Icon: User },
    { id: "access", label: t("iam.personDrawer.tabs.orgs"),  Icon: Building2, count: isAccessLoading || isAccessError ? undefined : memberOrgs.length },
    { id: "audit",  label: t("iam.personDrawer.tabs.audit"), Icon: FileText,  count: auditCount },
  ];

  return (
    <>
      <IamOverlayDrawer
        open={open}
        onOpenChange={(next) => { if (!next) handleClose(); }}
        title={user.displayName}
      >
        <div
          className={cn("flex min-h-0 flex-1 flex-col overflow-hidden bg-card", ORG_ACCENT_DRAWER_TOP_CLASS)}
          style={orgAccentVar(drawerAccentColor)}
        >
        {/* ── Head ─────────────────────────────────────────── */}
        <div
          className={cn(
            "flex items-start justify-between gap-2.5 border-b border-border/40 px-5 py-4",
            ORG_ACCENT_HEADER_WASH_CLASS,
          )}
          style={orgAccentVar(drawerAccentColor)}
        >
          <div className="flex min-w-0 items-center gap-3">
            <ImagePreviewTrigger src={user.photoUrl} alt={user.displayName}>
              <UserPhotoMark name={user.displayName} photoUrl={user.photoUrl} size="lg" />
            </ImagePreviewTrigger>
            <div className="min-w-0">
              <div className="flex min-w-0 items-center gap-1.5">
                <h3 className="truncate text-[15px] font-semibold leading-tight">{user.displayName}</h3>
                {isSuperTenantMember(access) && <SuperTenantBadge size={14} />}
              </div>
              <div className="flex items-center gap-1.5">
                <p className="truncate text-[12px] text-muted-foreground">{user.email}</p>
                <AuthProviderBadge provider={user.authProvider} variant="pill" />
              </div>
            </div>
          </div>
          <div className="flex shrink-0 items-center gap-1">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <RowActionsTrigger />
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48">
                <Tooltip>
                  <TooltipTrigger asChild>
                    <DropdownMenuItem className="gap-2" onClick={() => setTab("profile")}>
                      <Pencil className="h-3.5 w-3.5" />
                      {t("iam.personDrawer.actions.editProfile")}
                    </DropdownMenuItem>
                  </TooltipTrigger>
                  <TooltipContent side="left" className="max-w-[220px]">
                    {t("iam.personDrawer.actionTooltips.editProfile")}
                  </TooltipContent>
                </Tooltip>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <DropdownMenuItem
                      className="gap-2"
                      onClick={() => navigator.clipboard.writeText(user.email)}
                    >
                      <Copy className="h-3.5 w-3.5" />
                      {t("iam.personDrawer.actions.copyEmail")}
                    </DropdownMenuItem>
                  </TooltipTrigger>
                  <TooltipContent side="left" className="max-w-[220px]">
                    {t("iam.personDrawer.actionTooltips.copyEmail")}
                  </TooltipContent>
                </Tooltip>
                {onOpenAuditLog && canViewAuditLog && (
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <DropdownMenuItem
                        className="gap-2"
                        onClick={() => onOpenAuditLog({ searchPrefill: user.email })}
                      >
                        <ExternalLink className="h-3.5 w-3.5" />
                        {t("iam.personDrawer.actions.viewAuditLog")}
                      </DropdownMenuItem>
                    </TooltipTrigger>
                    <TooltipContent side="left" className="max-w-[220px]">
                      {t("iam.personDrawer.actionTooltips.viewAuditLog")}
                    </TooltipContent>
                  </Tooltip>
                )}
                {user.status === "DEACTIVATED" ? (
                  <>
                    <DropdownMenuSeparator />
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <DropdownMenuItem className="gap-2" onClick={onUnsuspend}>
                          <UserCheck className="h-3.5 w-3.5" />
                          {t("iam.personDrawer.actions.unsuspend")}
                        </DropdownMenuItem>
                      </TooltipTrigger>
                      <TooltipContent side="left" className="max-w-[220px]">
                        {t("iam.personDrawer.actionTooltips.unsuspend")}
                      </TooltipContent>
                    </Tooltip>
                  </>
                ) : user.status !== "INACTIVE" && (
                  <>
                    <DropdownMenuSeparator />
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <DropdownMenuItem
                          variant="destructive"
                          onClick={onSuspend}
                        >
                          <UserX className="h-3.5 w-3.5" />
                          {t("iam.personDrawer.actions.suspend")}
                        </DropdownMenuItem>
                      </TooltipTrigger>
                      <TooltipContent side="left" className="max-w-[220px]">
                        {t("iam.personDrawer.actionTooltips.suspend")}
                      </TooltipContent>
                    </Tooltip>
                  </>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
            <button
              onClick={handleClose}
              className="flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
              aria-label={t("iam.personDrawer.closePanel", "Close panel")}
            >
              <X className="h-3.5 w-3.5" />
            </button>
          </div>
        </div>

        {/* ── Facts row ────────────────────────────────────── */}
        <TooltipProvider delayDuration={300}>
        <div className="grid grid-cols-3 border-b border-border">
          {/* Status — always col 1 */}
          <DrawerStat
            label={t("iam.personDrawer.stats.status")}
            tooltip={t("iam.personDrawer.statTooltips.status")}
            valueClassName=""
            tooltipClassName="max-w-[240px]"
          >
            <UserStatusBadge status={user.status} label={t(`iam.personDrawer.status.${user.status}`, user.status)} variant="plain" />
          </DrawerStat>

          {user.pendingInvite ? (
            /* Invite link — spans cols 2+3 */
            <div className="col-span-2 flex flex-col gap-1.5 px-4 py-3">
              <span className="text-[10.5px] font-medium uppercase tracking-wide text-muted-foreground">{t("iam.personDrawer.stats.inviteLink")}</span>
              <div className="flex items-center gap-2">
                {/* Expiry badge */}
                {(() => {
                  const { tKey, count, severity } = expiresIn(user.pendingInvite!.expiresAt);
                  return (
                    <span className={cn(
                      "inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-medium tabular-nums",
                      severity === "expired" && "bg-destructive/10 text-destructive border-destructive/20",
                      severity === "warning" && "bg-warning-subtle text-warning-subtle-foreground border-warning-border",
                      severity === "ok"      && "bg-muted text-muted-foreground border-border",
                    )}>
                      <Clock className="h-2.5 w-2.5 shrink-0" />
                      {t(tKey, count !== undefined ? { count } : undefined)}
                    </span>
                  );
                })()}
                {/* Actions — pushed right: Copy (primary text) + Regenerate (icon-only, tooltip) */}
                <div className="ml-auto flex items-center gap-0.5">
                  {canCopyInviteLink && (
                    <CopyInviteButton
                      disabled={inviteLinkMutation.isPending}
                      onCopy={(callbacks) =>
                        inviteLinkMutation.mutate(user.pendingInvite!.inviteId, {
                          onSuccess: callbacks.onSuccess,
                          onError: callbacks.onError,
                        })
                      }
                      isPending={inviteLinkMutation.isPending}
                    />
                  )}
                  {canRegenerateInvite && (
                  <TooltipProvider delayDuration={300}>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <button
                          disabled={regenerateInviteMutation.isPending || inviteCooldown > 0}
                          onClick={() =>
                            regenerateInviteMutation.mutate(
                              { email: user.email },
                              { onSuccess: startInviteCooldown },
                            )
                          }
                          className={cn(
                            "inline-flex h-7 w-7 items-center justify-center rounded-md transition-colors",
                            "disabled:pointer-events-none disabled:opacity-40",
                            regenerateInviteMutation.isError
                              ? "text-destructive hover:bg-destructive/10"
                              : "text-muted-foreground hover:bg-muted hover:text-foreground",
                          )}
                        >
                          <RotateCcw className={cn("h-3.5 w-3.5 shrink-0", regenerateInviteMutation.isPending && "animate-spin")} />
                        </button>
                      </TooltipTrigger>
                      <TooltipContent side="bottom">
                        {regenerateInviteMutation.isPending
                          ? t("iam.personDrawer.invite.regenerating")
                          : inviteCooldown > 0
                          ? t("iam.personDrawer.invite.regenerateCooldown", { count: inviteCooldown })
                          : regenerateInviteMutation.isError
                          ? t("iam.personDrawer.invite.regenerateError")
                          : t("iam.personDrawer.invite.regenerate")}
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                  )}
                </div>
              </div>
            </div>
          ) : (
            <>
              {/* Last seen — col 2 */}
              <Tooltip>
                <TooltipTrigger asChild>
                  <div className="flex cursor-default flex-col gap-1 px-4 py-3 text-left">
                    <span className="text-[10.5px] font-medium uppercase tracking-wide text-muted-foreground">{t("iam.personDrawer.stats.lastSeen")}</span>
                    <span className="text-sm font-semibold tabular-nums">
                      {user.lastSeenAt ? relativeTimeI18n(user.lastSeenAt, t) : t("iam.personDrawer.stats.never")}
                    </span>
                  </div>
                </TooltipTrigger>
                <TooltipContent side="bottom" className="max-w-[240px]">{t("iam.personDrawer.statTooltips.lastSeen")}</TooltipContent>
              </Tooltip>
              {/* Orgs — col 3 */}
              <Tooltip>
                <TooltipTrigger asChild>
                  <div className="flex cursor-default flex-col gap-1 px-4 py-3 text-left">
                    <span className="text-[10.5px] font-medium uppercase tracking-wide text-muted-foreground">{t("iam.personDrawer.stats.organizations")}</span>
                    <span
                      className="text-sm font-semibold tabular-nums"
                      aria-label={t("iam.personDrawer.stats.organizations")}
                      aria-busy={isAccessLoading}
                    >
                      {isAccessLoading
                        ? <Skeleton className="h-4 w-6" />
                        : isAccessError
                          ? t("iam.personDrawer.orgs.loadErrorPlaceholder", "—")
                          : memberOrgs.length}
                    </span>
                  </div>
                </TooltipTrigger>
                <TooltipContent side="bottom" className="max-w-[240px]">{t("iam.personDrawer.statTooltips.organizations")}</TooltipContent>
              </Tooltip>
            </>
          )}
        </div>
        </TooltipProvider>

        {/* ── Tab strip ────────────────────────────────────── */}
        <DrawerTabStrip tabs={tabs} activeTab={tab} onTabChange={setTab} />

        {/* ── Body ─────────────────────────────────────────── */}
        {tab === "profile" ? (
          <PersonProfileForm user={user} activeTenantId={activeTenantId} variant="tab" />
        ) : (
        <div className="flex flex-1 flex-col gap-3 overflow-y-auto px-4 py-3">
        <div key={tab} className="flex flex-col gap-3 animate-in fade-in slide-in-from-bottom-1 duration-150">

          {/* Combined access tab — orgs as primary unit, roles inline */}
          {tab === "access" && (
            <div className="flex flex-col gap-1.5">
              {selfEditLocked && (
                <div className="flex items-start gap-2 rounded-md border border-border bg-muted/40 px-3 py-2 text-[12px] leading-snug text-muted-foreground">
                  <Lock className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                  <span>{t("iam.personDrawer.roles.selfEditBlocked")}</span>
                </div>
              )}

              {/* Header: org count + Add to all */}
              <div className="flex items-center justify-between pb-1">
                <span className="text-[11px] font-medium text-muted-foreground">
                  {isAccessLoading
                    ? <Skeleton className="h-3 w-16" />
                    : isAccessError
                      ? null
                      : t("iam.personDrawer.orgs.memberCount", { member: memberOrgs.length, total: allTenants.length })}
                </span>
                {joinableTenants.length > 1 && canEditOrgMembership && (
                  <TooltipProvider delayDuration={200}>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <button
                          disabled={bulkPending || orgTogglePending}
                          onClick={() => setConfirmAddAllOpen(true)}
                          className="flex items-center gap-1 text-[11px] font-medium text-primary hover:underline disabled:opacity-40"
                        >
                          {bulkPending && <Loader2 className="h-3 w-3 animate-spin" />}
                          {t("iam.personDrawer.orgs.addToAll")}
                        </button>
                      </TooltipTrigger>
                      <TooltipContent side="bottom" className="max-w-[280px]">
                        {t("iam.personDrawer.orgs.addToAllTip", { count: joinableTenants.length })}
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                )}
              </div>

              {fixedRoles.length > 0 && !selfEditLocked && (
                <div className="flex flex-col gap-1.5 pb-1">
                  <span className="text-[11px] font-medium text-muted-foreground">
                    {t("iam.personDrawer.orgs.fixedRolesHeading", "Fixed roles")}
                  </span>
                  <div className="flex flex-wrap gap-1.5">
                    {fixedRoles.map((role) => {
                      const isLastAccess = access.every(
                        (entry) => catalogTemplateId(entry.access) === role.templateId,
                      );
                      const isPending = pendingFixedRevoke?.templateId === role.templateId;
                      const isRevoking = revokeTemplate.isPending && revokeTemplate.variables?.templateId === role.templateId;
                      return (
                        <div key={role.templateId} className="flex items-center">
                          <span className={cn(
                            "flex h-6 items-center rounded-full px-2.5 text-[11px] font-medium",
                            isPending ? "bg-destructive/15 text-destructive" : "bg-muted/80 text-muted-foreground",
                          )}>
                            {role.name}
                          </span>
                          {canRevokeTemplate && (
                            <TooltipProvider delayDuration={200}>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <span>
                                    <button
                                      type="button"
                                      disabled={isLastAccess || entryPending}
                                      onClick={() => {
                                        if (isLastAccess) return;
                                        setPendingOrgRemove(null);
                                        setPendingAccessRemove(null);
                                        setPendingRoleEdit(null);
                                        setPendingFixedRevoke(isPending ? null : role);
                                      }}
                                      className={cn(
                                        "ml-0.5 flex h-6 w-6 items-center justify-center rounded transition-colors disabled:cursor-not-allowed disabled:opacity-40",
                                        isPending ? "bg-destructive/10 text-destructive" : "text-muted-foreground hover:bg-destructive/10 hover:text-destructive",
                                      )}
                                      aria-label={t("iam.personDrawer.orgs.removeFixedRole", { role: role.name })}
                                      aria-busy={isRevoking}
                                    >
                                      {isRevoking ? <Loader2 className="h-3 w-3 animate-spin" /> : <X className="h-3 w-3" />}
                                    </button>
                                  </span>
                                </TooltipTrigger>
                                <TooltipContent side="bottom" className="max-w-[260px]">
                                  {isLastAccess
                                    ? t("iam.personDrawer.orgs.removeFixedLastAccess")
                                    : t("iam.personDrawer.orgs.fixedGrantedBy", { role: role.name })}
                                </TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                          )}
                        </div>
                      );
                    })}
                  </div>
                  {pendingFixedRevoke && (
                    <div className="animate-in fade-in slide-in-from-top-1 duration-150 rounded-lg border border-destructive/20 bg-destructive/5 px-3 py-2.5" role="region" aria-label={t("iam.personDrawer.orgs.removeFixedRole", { role: pendingFixedRevoke.name })}>
                      <p className="mb-2.5 text-[11px] text-muted-foreground">
                        {t("iam.personDrawer.orgs.removeFixedConfirmPrefix")}
                        <strong className="font-medium text-foreground">{pendingFixedRevoke.name}</strong>
                        {t("iam.personDrawer.orgs.removeFixedConfirmSuffix", { count: pendingFixedRevoke.orgIds.length })}
                      </p>
                      <div className="flex items-center gap-2">
                        <Button disabled={revokeTemplate.isPending} onClick={handleFixedRevokeConfirm} variant="destructive" size="sm" className="h-6 px-2.5 text-[11px]">
                          {revokeTemplate.isPending && <Loader2 className="h-3 w-3 animate-spin" />}
                          {t("iam.personDrawer.orgs.remove")}
                        </Button>
                        <Button type="button" variant="ghost" size="sm" className="h-6 px-2.5 text-[11px]" disabled={revokeTemplate.isPending} onClick={() => setPendingFixedRevoke(null)}>
                          {t("iam.personDrawer.orgs.cancel")}
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {isAccessLoading ? (
                <div className="flex flex-col gap-2" aria-busy="true">
                  <Skeleton className="h-14 w-full rounded-lg" />
                  <Skeleton className="h-14 w-full rounded-lg" />
                </div>
              ) : isAccessError ? (
                <div className="flex flex-col items-start gap-2 rounded-lg border border-destructive/30 bg-destructive/5 px-3 py-2" role="alert">
                  <p className="text-xs text-destructive">{t("iam.personDrawer.orgs.loadError", "Could not load organizations.")}</p>
                  {onRetryAccess && (
                    <Button type="button" variant="outline" size="sm" className="h-6 px-2.5 text-[11px]" onClick={onRetryAccess}>
                      {t("iam.personDrawer.orgs.loadErrorRetry", "Try again")}
                    </Button>
                  )}
                </div>
              ) : memberOrgs.length === 0 ? (
                <div className="flex flex-col items-center gap-2 rounded-lg border border-dashed border-border bg-muted/10 py-8 text-center">
                  <div className="flex h-10 w-10 items-center justify-center rounded-full bg-muted">
                    <Building2 className="h-5 w-5 text-muted-foreground" />
                  </div>
                  <p className="text-sm font-medium">{t("iam.personDrawer.orgs.emptyTitle")}</p>
                  <p className="px-6 text-xs text-muted-foreground">{t("iam.personDrawer.orgs.emptyHint")}</p>
                </div>
              ) : (
                <>
                  {(accessExpanded ? memberOrgs : memberOrgs.slice(0, ACCESS_ORG_VISIBLE_DEFAULT)).map((org) => {
                    const locked = lockedTenantId === org.id;
                    const tenantMeta = allTenants.find((t) => t.id === org.id);
                    const isOrgRemoving = pendingOrgRemove?.tenantId === org.id;
                    const isRemovingOrg = orgTogglePending && removingOrgId === org.id;
                    const isRoleEditing = pendingRoleEdit?.access.tenantId === org.id;
                    const isAccessRemoving = pendingAccessRemove?.entry.access.tenantId === org.id;
                    const isSingleRole = org.roles.length === 1;
                    const singleEntry = isSingleRole ? org.roles[0] : null;
                    const canRemoveOrg = org.roles.every((entry) => isPerTenantAssignable(entry.access));
                    const canSwitchSingle = Boolean(singleEntry && isPerTenantAssignable(singleEntry.access));
                    const replaceRoleName = (roleName: string) =>
                      t("iam.personDrawer.roles.replaceRoleNamed", {
                        role: roleName,
                        org: org.name,
                        defaultValue: "Replace role {{role}} in {{org}}",
                      });
                    const fixedRoleName = org.roles.find((entry) => isFixedCatalogAccess(entry.access))?.access.name;
                    const isSuperTenantOrg = org.type === "SUPER_TENANT";
                    const orgAccent = tenantAccentColor({
                      name: org.name,
                      brandColor: tenantMeta?.brandColor,
                    });
                    const isRowBusy = isOrgRemoving || isAccessRemoving || isRemovingOrg || isRoleEditing;
                    const showOrgAccent = !isSuperTenantOrg && !isRowBusy;
                    const showSuperTenantAccent = isSuperTenantOrg && !isRowBusy;

                    return (
                      <div key={org.id} className="flex flex-col gap-1">
                        <div
                          className={cn(
                            "group/org relative flex items-center gap-3 overflow-hidden rounded-lg border px-3 py-2.5 transition-colors",
                            isOrgRemoving || isAccessRemoving || isRemovingOrg
                              ? "border-destructive/30 bg-destructive/5"
                              : isRoleEditing
                                ? "border-primary/30 bg-primary/5"
                                : showSuperTenantAccent
                                  ? "border-amber-500/20 bg-card hover:border-amber-500/30 dark:border-amber-400/20 dark:hover:border-amber-400/30"
                                  : showOrgAccent
                                    ? "border bg-card hover:bg-muted/20"
                                    : "border-border bg-card hover:bg-muted/40",
                          )}
                          style={showOrgAccent ? orgAccentRowSurfaceStyle(orgAccent) : undefined}
                        >
                          {showOrgAccent && (
                            <>
                              <div
                                aria-hidden
                                className="pointer-events-none absolute inset-0 rounded-lg dark:hidden"
                                style={orgAccentRowWashStyle(orgAccent)}
                              />
                              <div
                                aria-hidden
                                className="pointer-events-none absolute inset-0 hidden rounded-lg dark:block"
                                style={orgAccentRowWashStyleDark(orgAccent)}
                              />
                            </>
                          )}
                          {showSuperTenantAccent && (
                            <>
                              <div
                                aria-hidden
                                className="iam-super-tenant-card__mesh pointer-events-none absolute inset-0 rounded-lg motion-reduce:opacity-80"
                              />
                              <div
                                aria-hidden
                                className="pointer-events-none absolute inset-0 overflow-hidden rounded-lg motion-reduce:hidden"
                              >
                                <div className="iam-super-tenant-card__shimmer absolute inset-y-0 -left-1/2 w-1/2" />
                              </div>
                            </>
                          )}
                          {/* Logo + name + roles */}
                          <div
                            role={onJumpToOrg ? "button" : undefined}
                            tabIndex={onJumpToOrg ? 0 : undefined}
                            onClick={() => onJumpToOrg?.(org.id)}
                            onKeyDown={(e) => { if (onJumpToOrg && (e.key === "Enter" || e.key === " ")) { e.preventDefault(); onJumpToOrg(org.id); } }}
                            className={cn(
                              "relative z-[1] flex min-w-0 flex-1 items-center gap-3 text-left",
                              onJumpToOrg && "cursor-pointer",
                            )}
                          >
                            <ImagePreviewTrigger src={tenantMeta?.logoUrl} alt={org.name}>
                              <OrgChip
                                name={org.name}
                                size={28}
                                logoUrl={tenantMeta?.logoUrl}
                                accent={orgAccent}
                                isSuperTenant={isSuperTenantOrg}
                              />
                            </ImagePreviewTrigger>
                            <div className="flex min-w-0 flex-1 flex-col gap-0.5">
                              <span className="truncate text-sm font-medium">{org.name}</span>
                              {isSingleRole && singleEntry && (
                                <span className="truncate text-[11px] text-muted-foreground">{singleEntry.access.name}</span>
                              )}
                              {!isSingleRole && (
                                <div className="flex flex-wrap gap-1 pt-0.5">
                                  {org.roles.map((entry) => {
                                    const isEditingChip = pendingRoleEdit?.id === entry.id;
                                    const isRemovingChip = pendingAccessRemove?.entry.id === entry.id;
                                    return (
                                      <div key={entry.id} className="group/chip flex items-center">
                                        <span className={cn(
                                          "flex h-5 items-center rounded-full px-2 text-[10.5px] font-medium transition-colors",
                                          isEditingChip ? "bg-primary/15 text-primary" : isRemovingChip ? "bg-destructive/15 text-destructive" : "bg-muted/80 text-muted-foreground",
                                        )}>
                                          {entry.access.name}
                                        </span>
                                        {!selfEditLocked && canReplaceAccess && isPerTenantAssignable(entry.access) && (
                                          <button
                                            type="button"
                                            disabled={entryPending}
                                            aria-label={replaceRoleName(entry.access.name)}
                                            onClick={(e) => { e.stopPropagation(); handleAccessEditClick(entry); }}
                                            className="ml-0.5 inline-flex min-h-11 items-center rounded px-1.5 text-[10px] font-medium text-primary hover:bg-primary/10 disabled:opacity-40 [@media(hover:hover)_and_(pointer:fine)]:h-5 [@media(hover:hover)_and_(pointer:fine)]:min-h-5"
                                          >
                                            {t("iam.personDrawer.roles.replaceRole", "Replace role")}
                                          </button>
                                        )}
                                      </div>
                                    );
                                  })}
                                </div>
                              )}
                            </div>
                          </div>

                          {/* Right controls */}
                          {locked || selfEditLocked ? (
                            <TooltipProvider delayDuration={200}>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <span className="relative z-[1] flex h-6 w-6 items-center justify-center rounded text-muted-foreground/60">
                                    <Lock className="h-3 w-3" />
                                  </span>
                                </TooltipTrigger>
                                <TooltipContent side="left">
                                  {selfEditLocked ? t("iam.personDrawer.roles.selfEditLocked") : t("iam.personDrawer.orgs.lockedActiveOrg")}
                                </TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                          ) : !canRemoveOrg ? (
                            <TooltipProvider delayDuration={200}>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <span className="relative z-[1] flex h-6 w-6 items-center justify-center rounded text-muted-foreground/60">
                                    <Lock className="h-3 w-3" />
                                  </span>
                                </TooltipTrigger>
                                <TooltipContent side="left" className="max-w-[260px]">
                                  {t("iam.personDrawer.orgs.fixedGrantedBy", { role: fixedRoleName ?? org.roles[0]?.access.name ?? "" })}
                                </TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                          ) : (
                            <div className="relative z-[1] flex items-center gap-1">
                              {isSingleRole && singleEntry && canReplaceAccess && canSwitchSingle && (
                                <Button
                                  type="button"
                                  variant="outline"
                                  size="sm"
                                  className="h-6 min-h-11 px-2 text-[11px] [@media(hover:hover)_and_(pointer:fine)]:min-h-6"
                                  disabled={entryPending}
                                  aria-label={replaceRoleName(singleEntry.access.name)}
                                  aria-pressed={isRoleEditing}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    if (isRoleEditing) { setPendingRoleEdit(null); return; }
                                    setPendingOrgRemove(null);
                                    setPendingAccessRemove(null);
                                    handleAccessEditClick(singleEntry);
                                  }}
                                >
                                  {t("iam.personDrawer.roles.replaceRole", "Replace role")}
                                </Button>
                              )}
                              {memberOrgs.length > 1 && canRemoveOrg && (
                                <button
                                  type="button"
                                  disabled={orgTogglePending || entryPending}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    if (isRemovingOrg) return;
                                    if (isOrgRemoving) { setPendingOrgRemove(null); return; }
                                    setPendingRoleEdit(null);
                                    setPendingAccessRemove(null);
                                    setPendingOrgRemove({ tenantId: org.id, tenantName: org.name, roleCount: org.roles.length });
                                  }}
                                  className={cn(
                                    "flex h-6 w-6 items-center justify-center rounded transition-all disabled:cursor-not-allowed disabled:opacity-40",
                                    isOrgRemoving || isRemovingOrg ? "bg-destructive/10 text-destructive" : "text-muted-foreground hover:bg-destructive/10 hover:text-destructive",
                                  )}
                                  aria-label={isOrgRemoving ? t("iam.personDrawer.orgs.cancel") : t("iam.personDrawer.orgs.removeFromOrg", { org: org.name })}
                                  aria-busy={isRemovingOrg}
                                >
                                  {isRemovingOrg ? (
                                    <Loader2 className="h-3 w-3 animate-spin text-destructive" />
                                  ) : (
                                    <X className="h-3 w-3" />
                                  )}
                                </button>
                              )}
                            </div>
                          )}
                        </div>

                        {/* Role switch panel (pencil — single role, or chip pencil) */}
                        {isRoleEditing && pendingRoleEdit && (
                          replaceChoices.length > 0 ? (
                            <div
                              className="animate-in fade-in slide-in-from-top-1 duration-150 rounded-lg border border-primary/25 bg-primary/5 px-3 py-2.5"
                              role="region"
                              aria-label={t("iam.personDrawer.roles.replaceRoleIn", { org: org.name, defaultValue: "Replace role in {{org}}" })}
                              aria-busy={entryPending}
                            >
                              <p className="mb-2 text-[11px] font-medium text-muted-foreground">
                                {t("iam.personDrawer.roles.replaceRoleIn", { org: org.name, defaultValue: "Replace role in {{org}}" })}
                              </p>
                              <div
                                className={cn(
                                  "mb-2.5 flex flex-wrap items-center gap-1.5 transition-opacity",
                                  entryPending && "pointer-events-none opacity-50",
                                )}
                              >
                                <span className="shrink-0 rounded-md border border-border bg-card px-2 py-1 text-xs font-medium text-muted-foreground">
                                  {pendingRoleEdit.access.name}
                                </span>
                                <ArrowRight className="h-3 w-3 shrink-0 text-muted-foreground/40" aria-hidden />
                                {replaceChoices.map((choice) => (
                                  <button
                                    key={choice.id}
                                    type="button"
                                    disabled={entryPending}
                                    onClick={() => setSelectedRoleForEdit(selectedRoleForEdit?.id === choice.id ? null : choice)}
                                    className={cn(
                                      "rounded-full border px-2.5 py-1 text-xs font-medium transition-colors disabled:opacity-40",
                                      selectedRoleForEdit?.id === choice.id
                                        ? "border-primary/60 bg-primary/10 text-primary"
                                        : "border-border bg-card hover:border-primary/60 hover:bg-primary/10 hover:text-primary",
                                    )}
                                  >
                                    {choice.name}
                                  </button>
                                ))}
                              </div>
                              <div className="flex items-center justify-end gap-2">
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="sm"
                                  className="h-6 px-2.5 text-[11px]"
                                  disabled={entryPending}
                                  onClick={() => setPendingRoleEdit(null)}
                                >
                                  {t("iam.personDrawer.roles.cancel")}
                                </Button>
                                {selectedRoleForEdit && (
                                  <Button
                                    size="sm"
                                    className="h-6 gap-1.5 px-2.5 text-[11px]"
                                    disabled={entryPending}
                                    onClick={() => handleAccessRoleSwitch(selectedRoleForEdit)}
                                  >
                                    {entryPending && <Loader2 className="h-3 w-3 animate-spin" />}
                                    {entryPending
                                      ? t("iam.personDrawer.roles.replacingRole", "Replacing…")
                                      : t("iam.personDrawer.roles.confirmReplace", { role: selectedRoleForEdit.name, defaultValue: "Replace with {{role}}" })}
                                  </Button>
                                )}
                              </div>
                            </div>
                          ) : wildcardTemplatesLoading ? (
                            <div
                              className="animate-in fade-in slide-in-from-top-1 duration-150 flex items-center gap-2 rounded-lg border border-primary/25 bg-primary/5 px-3 py-2.5"
                              role="region"
                              aria-label={t("iam.personDrawer.roles.replaceRoleIn", { org: org.name, defaultValue: "Replace role in {{org}}" })}
                              aria-busy="true"
                            >
                              <Loader2 className="h-3 w-3 animate-spin text-muted-foreground" />
                            </div>
                          ) : (
                            <div
                              className="animate-in fade-in slide-in-from-top-1 duration-150 rounded-lg border border-border bg-muted/30 px-3 py-2.5"
                              role="region"
                              aria-label={t("iam.personDrawer.roles.noGrantableRoles", "No roles you can grant")}
                            >
                              <p className="mb-1 text-[12px] font-medium text-foreground">
                                {t("iam.personDrawer.roles.noGrantableRoles", "No roles you can grant")}
                              </p>
                              <p className="mb-3 text-[11px] leading-relaxed text-muted-foreground">
                                {isSuperTenant
                                  ? t(
                                      "iam.personDrawer.roles.assignFixedHint",
                                      "Fixed roles on this organization are assigned from People → Assign Role. Wildcard roles can be granted onto any organization.",
                                    )
                                  : t("iam.personDrawer.roles.onlyRoleBody", { role: pendingRoleEdit.access.name })}
                              </p>
                              <div className="flex items-center justify-end gap-2">
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="sm"
                                  className="h-6 px-2.5 text-[11px]"
                                  disabled={entryPending}
                                  onClick={() => setPendingRoleEdit(null)}
                                >
                                  {t("iam.personDrawer.roles.cancel")}
                                </Button>
                                {onCreateRole && (
                                  <Button
                                    type="button"
                                    size="sm"
                                    className="h-6 px-2.5 text-[11px]"
                                    onClick={() => {
                                      const tenantId = pendingRoleEdit.access.tenantId;
                                      setPendingRoleEdit(null);
                                      onCreateRole(tenantId);
                                    }}
                                  >
                                    {t("iam.personDrawer.roles.createRole")}
                                  </Button>
                                )}
                              </div>
                            </div>
                          )
                        )}

                        {/* Individual role remove confirm (last role in org, no alternatives) */}
                        {isAccessRemoving && pendingAccessRemove && (
                          <div className="animate-in fade-in slide-in-from-top-1 duration-150 rounded-lg border border-destructive/20 bg-destructive/5 px-3 py-2.5" role="region" aria-label={t("iam.personDrawer.roles.remove")}>
                            <p className="mb-2.5 text-[11px] text-muted-foreground">
                              {t("iam.personDrawer.roles.removeLastRolePrefix")}
                              <strong className="font-medium text-foreground">{pendingAccessRemove.entry.access.tenant.name}</strong>
                              {t("iam.personDrawer.roles.removeLastRoleSuffix")}
                            </p>
                            <div className="flex items-center gap-2">
                              <Button disabled={entryPending} onClick={handleAccessRemoveConfirm} variant="destructive" size="sm" className="h-6 px-2.5 text-[11px]">
                                {entryPending && <Loader2 className="h-3 w-3 animate-spin" />}
                                {t("iam.personDrawer.roles.remove")}
                              </Button>
                              <Button type="button" variant="ghost" size="sm" className="h-6 px-2.5 text-[11px]" disabled={entryPending} onClick={() => setPendingAccessRemove(null)}>
                                {t("iam.personDrawer.roles.cancel")}
                              </Button>
                            </div>
                          </div>
                        )}

                        {/* Org remove confirm */}
                        {isOrgRemoving && pendingOrgRemove && (
                          <div className="animate-in fade-in slide-in-from-top-1 duration-150 rounded-lg border border-destructive/20 bg-destructive/5 px-3 py-2.5" role="region" aria-label={t("iam.personDrawer.orgs.removeFromOrg", { org: pendingOrgRemove.tenantName })}>
                            <p className="mb-2.5 text-[11px] text-muted-foreground">
                              {t("iam.personDrawer.orgs.removeConfirmPrefix")}
                              <strong className="font-medium text-foreground">{pendingOrgRemove.tenantName}</strong>
                              {pendingOrgRemove.roleCount === 1
                                ? t("iam.personDrawer.orgs.removeConfirmSuffixSingular")
                                : t("iam.personDrawer.orgs.removeConfirmSuffixPlural", { count: pendingOrgRemove.roleCount })}
                            </p>
                            <div className="flex items-center gap-2">
                              <Button disabled={orgTogglePending} onClick={() => void handleOrgRemoveConfirm()} variant="destructive" size="sm" className="h-6 px-2.5 text-[11px]">
                                {orgTogglePending && <Loader2 className="h-3 w-3 animate-spin" />}
                                {t("iam.personDrawer.orgs.remove")}
                              </Button>
                              <Button type="button" variant="ghost" size="sm" className="h-6 px-2.5 text-[11px]" disabled={orgTogglePending} onClick={() => setPendingOrgRemove(null)}>
                                {t("iam.personDrawer.orgs.cancel")}
                              </Button>
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}

                  {!accessExpanded && memberOrgs.length > ACCESS_ORG_VISIBLE_DEFAULT && (
                    <button
                      onClick={() => setAccessExpanded(true)}
                      className="flex w-full items-center justify-center gap-1.5 rounded-lg border border-dashed border-border py-2 text-xs font-medium text-muted-foreground transition-colors hover:border-border hover:bg-muted/40 hover:text-foreground"
                    >
                      <ChevronDown className="h-3 w-3" />
                      {t("iam.personDrawer.roles.showMore", { count: memberOrgs.length - ACCESS_ORG_VISIBLE_DEFAULT })}
                    </button>
                  )}
                  {accessExpanded && memberOrgs.length > ACCESS_ORG_VISIBLE_DEFAULT && (
                    <button
                      onClick={() => setAccessExpanded(false)}
                      className="flex w-full items-center justify-center gap-1.5 py-1 text-xs text-muted-foreground transition-colors hover:text-foreground"
                    >
                      {t("iam.personDrawer.roles.showLess")}
                    </button>
                  )}
                </>
              )}

              {/* Add organization combobox */}
              {joinableTenants.length > 0 && canEditOrgMembership && (
                <div className="pt-1">
                  <SearchableCombobox
                    items={joinableTenants}
                    getKey={(tenant) => tenant.id}
                    getLabel={(tenant) => tenant.name}
                    onSelect={handleOrgAdd}
                    open={orgComboboxOpen}
                    onOpenChange={setOrgComboboxOpen}
                    closeOnSelect={false}
                    selectingKey={entryGrant.isPending ? entryGrant.variables?.tenantId ?? null : null}
                    disabled={orgTogglePending || entryGrant.isPending}
                    searchPlaceholder={t("iam.personDrawer.orgs.searchOrgs")}
                    emptyMessage={t("iam.personDrawer.orgs.noMatchingOrgs")}
                    contentClassName={cn(
                      "border-2 border-border bg-card p-0 shadow-lg",
                      "ring-1 ring-black/[0.04] dark:ring-white/[0.1] dark:shadow-[0_16px_48px_-12px_rgba(0,0,0,0.72)]",
                      "[&_[data-slot=command]]:bg-card [&_[data-slot=command-input-wrapper]]:border-border/70",
                    )}
                    renderTrigger={({ open }) => (
                      <button
                        type="button"
                        disabled={orgTogglePending || entryGrant.isPending}
                        aria-expanded={open}
                        className={cn(
                          "flex w-full items-center gap-2 rounded-lg border border-dashed border-border bg-card px-3 py-2.5 text-[12.5px] font-medium text-muted-foreground transition-colors",
                          "hover:border-primary/40 hover:bg-primary/5 hover:text-primary",
                          "disabled:pointer-events-none disabled:opacity-40",
                          open && "border-primary/40 bg-primary/5 text-primary",
                        )}
                      >
                        {entryGrant.isPending ? (
                          <Loader2 className="h-3.5 w-3.5 shrink-0 animate-spin" />
                        ) : (
                          <Plus className="h-3.5 w-3.5 shrink-0" />
                        )}
                        <span className="flex-1 text-left">{t("iam.personDrawer.orgs.addOrg")}</span>
                        <Search className="h-3 w-3 shrink-0 opacity-60" />
                      </button>
                    )}
                    itemClassName={cn(
                      "group/org-opt mb-1 cursor-pointer overflow-hidden border transition-[border-color,box-shadow] duration-150",
                      "hover:border-[color-mix(in_srgb,var(--org-accent)_38%,var(--border))] hover:shadow-sm",
                      "dark:hover:border-[color-mix(in_srgb,var(--org-accent)_48%,var(--border))]",
                      "dark:hover:shadow-[0_0_0_1px_color-mix(in_srgb,var(--org-accent)_28%,transparent),0_2px_8px_-2px_color-mix(in_srgb,var(--org-accent)_22%,transparent)]",
                      "data-[selected=true]:border-[color-mix(in_srgb,var(--org-accent)_42%,var(--border))] data-[selected=true]:shadow-sm",
                      "dark:data-[selected=true]:border-[color-mix(in_srgb,var(--org-accent)_55%,var(--border))]",
                      "dark:data-[selected=true]:shadow-[0_0_0_1px_color-mix(in_srgb,var(--org-accent)_32%,transparent),0_2px_8px_-2px_color-mix(in_srgb,var(--org-accent)_24%,transparent)]",
                      "data-[selected=true]:bg-transparent data-[selected=true]:text-foreground",
                      "[&>svg]:relative [&>svg]:z-[1]",
                    )}
                    getItemStyle={(tenant) => orgAccentRowSurfaceStyle(
                      tenant.type === "SUPER_TENANT" ? SUPER_TENANT_ACCENT : tenantAccentColor(tenant),
                    )}
                    renderItem={(tenant) => {
                      const isSuperTenantOption = tenant.type === "SUPER_TENANT";
                      const accent = isSuperTenantOption ? SUPER_TENANT_ACCENT : tenantAccentColor(tenant);
                      return (
                        <>
                          {isSuperTenantOption ? (
                            <>
                              <div
                                aria-hidden
                                className="iam-super-tenant-card__mesh pointer-events-none absolute inset-0 rounded-sm motion-reduce:opacity-80"
                              />
                              <div
                                aria-hidden
                                className="pointer-events-none absolute inset-0 overflow-hidden rounded-sm motion-reduce:hidden"
                              >
                                <div className="iam-super-tenant-card__shimmer absolute inset-y-0 -left-1/2 w-1/2" />
                              </div>
                            </>
                          ) : (
                            <>
                              <div
                                aria-hidden
                                className="pointer-events-none absolute inset-0 rounded-sm opacity-85 transition-opacity duration-150 group-hover/org-opt:opacity-100 group-data-[selected=true]/org-opt:opacity-100 dark:hidden"
                                style={orgAccentRowWashStyle(accent)}
                              />
                              <div
                                aria-hidden
                                className="pointer-events-none absolute inset-0 hidden rounded-sm dark:block"
                                style={orgAccentRowWashStyleDark(accent)}
                              />
                              <div
                                aria-hidden
                                className="pointer-events-none absolute inset-0 hidden rounded-sm opacity-0 transition-opacity duration-150 group-hover/org-opt:opacity-100 group-data-[selected=true]/org-opt:opacity-100 dark:block"
                                style={orgAccentRowWashStyleDarkHover(accent)}
                              />
                            </>
                          )}
                          <div className="relative z-[1] flex min-w-0 flex-1 items-center gap-2.5">
                            <OrgChip
                              name={tenant.name}
                              size={20}
                              logoUrl={tenant.logoUrl}
                              accent={accent}
                              isSuperTenant={isSuperTenantOption}
                            />
                            <span className="min-w-0 flex-1 truncate text-[13px]">{tenant.name}</span>
                            <span className="shrink-0 text-[10.5px] text-muted-foreground">
                              {tenant.roles.length === 1
                                ? tenant.roles[0].name
                                : t("iam.personDrawer.orgs.rolesAvailable", { count: tenant.roles.length })}
                            </span>
                          </div>
                        </>
                      );
                    }}
                  />

                  {pendingRolePick && !orgMap.has(pendingRolePick.tenant.id) && (
                    <div className="animate-in fade-in slide-in-from-top-1 duration-150 mt-1.5 rounded-lg border border-primary/25 bg-primary/5 px-3 py-2.5">
                      <p className="mb-2 text-[11px] font-medium text-muted-foreground">
                        {t("iam.personDrawer.orgs.chooseRole", { org: pendingRolePick.tenant.name })}
                      </p>
                      <div className="mb-2 flex flex-wrap gap-1.5">
                        {pendingRolePick.groups.map((g) => {
                          const isAddingRole = isAddingGroup(g);
                          return (
                          <button
                            key={g.id}
                            type="button"
                            disabled={entryPending}
                            aria-busy={isAddingRole}
                            onClick={() => {
                              grantOrgRole(g, () => setPendingRolePick(null));
                            }}
                            className={cn(
                              "inline-flex items-center gap-1.5 rounded-full border border-border bg-card px-2.5 py-1 text-xs font-medium transition-colors hover:border-primary/60 hover:bg-primary/10 hover:text-primary disabled:cursor-not-allowed disabled:opacity-50",
                              isAddingRole && "border-primary/60 bg-primary/10 text-primary",
                            )}
                          >
                            {isAddingRole && <Loader2 className="h-3 w-3 animate-spin" />}
                            {g.name}
                          </button>
                          );
                        })}
                      </div>
                      <Button type="button" variant="ghost" size="sm" className="h-6 px-2.5 text-[11px]" onClick={() => setPendingRolePick(null)}>
                        {t("iam.personDrawer.orgs.cancel")}
                      </Button>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Audit tab */}
          {tab === "audit" && (
            <AuditTimeline
              scope="user"
              scopeId={user?.id ?? ""}
              hasPermission={canViewAuditLog}
              onEntryClick={onOpenAuditLog ? (id) => onOpenAuditLog({ highlightId: id }) : undefined}
            />
          )}
        </div>
        </div>
        )}

        {/* ── Footer (deactivated users only) ──────────────── */}
        {user.status === "DEACTIVATED" && tab !== "profile" && (
          <div className="flex items-center justify-end border-t border-border px-4 py-3">
            <div className="flex items-center gap-2">
              <Button size="sm" variant="outline" className="gap-1.5" onClick={onUnsuspend}>
                <UserCheck className="h-3.5 w-3.5" />
                {t("iam.personDrawer.actions.unsuspend")}
              </Button>
            </div>
          </div>
        )}
        </div>
      </IamOverlayDrawer>

      <Dialog open={confirmAddAllOpen} onOpenChange={setConfirmAddAllOpen}>
        <DialogContent className="flex max-h-[min(560px,calc(100vh-4rem))] w-full max-w-[460px] flex-col gap-0 overflow-hidden p-0" {...PREVENT_DISMISS_PROPS}>
          <DialogHeader className="space-y-1.5 px-6 pt-6 pb-4">
            <DialogTitle className="text-[15px] font-semibold tracking-tight text-foreground">
              {t("iam.personDrawer.orgs.addAllConfirmTitle")}
            </DialogTitle>
            <DialogDescription className="text-[12.5px] leading-relaxed text-muted-foreground">
              {t("iam.personDrawer.orgs.addAllConfirmDescription", {
                name: user?.displayName ?? "",
                count: joinableTenants.length,
              })}
            </DialogDescription>
          </DialogHeader>

          <div className="min-h-0 flex-1 overflow-hidden px-6">
            <div className="flex items-baseline justify-between pb-1.5">
              <span className="text-[10.5px] font-medium uppercase tracking-wide text-muted-foreground">
                {t("iam.personDrawer.orgs.addAllConfirmListLabel")}
              </span>
              <span className="text-[10.5px] font-medium uppercase tracking-wide text-muted-foreground">
                {t("iam.personDrawer.orgs.addAllConfirmRoleLabel")}
              </span>
            </div>
            <ul
              className="max-h-[240px] divide-y divide-border/60 overflow-y-auto rounded-lg border border-border bg-muted/20"
              aria-label={t("iam.personDrawer.orgs.addAllConfirmListLabel")}
            >
              {[...joinableTenants]
                .sort((a, b) => a.name.localeCompare(b.name))
                .map((tenant) => {
                  const accent = tenantAccentColor(tenant);
                  return (
                    <li
                      key={tenant.id}
                      className="flex items-center gap-2.5 px-3 py-2"
                      style={{
                        background: `linear-gradient(to right, color-mix(in srgb, ${accent} 16%, transparent) 0%, color-mix(in srgb, ${accent} 5%, transparent) 38%, transparent 68%)`,
                      }}
                    >
                      <OrgChip
                        name={tenant.name}
                        size={22}
                        logoUrl={tenant.logoUrl}
                        accent={accent}
                      />
                      <span className="min-w-0 flex-1 truncate text-[13px] text-foreground">
                        {tenant.name}
                      </span>
                      <span className="shrink-0 truncate pl-2 text-right text-[11.5px] text-muted-foreground">
                        {tenant.roles[0]?.name}
                      </span>
                    </li>
                  );
                })}
            </ul>
          </div>

          <div className="flex items-center justify-between gap-3 px-6 pt-4 pb-5">
            <p className="text-[11.5px] leading-snug text-muted-foreground">
              {t("iam.personDrawer.orgs.addAllConfirmBody")}
            </p>
            <div className="flex shrink-0 items-center gap-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setConfirmAddAllOpen(false)}
                disabled={bulkPending}
              >
                {t("iam.personDrawer.orgs.cancel")}
              </Button>
              <Button
                size="sm"
                disabled={bulkPending}
                onClick={handleAddToAll}
                className="gap-1.5"
              >
                {bulkPending && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
                {bulkPending
                  ? t("iam.personDrawer.orgs.addAllPending")
                  : t("iam.personDrawer.orgs.addAllConfirm", { count: joinableTenants.length })}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
